import { cva, type VariantProps } from "class-variance-authority";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

type LoaderSize = "xxs" | "xs" | "sm" | "md" | "lg";
type LoaderLayout = "page" | "inline";

type LoaderProps = {
  className?: string;
  size?: LoaderSize;
  layout?: LoaderLayout;
  label?: string;
  showLabel?: boolean;
  intervalMs?: number;
};

const QUICK_ACTION_ICONS = [
  { src: "/icons/quick-actions/activity.png", alt: "Activity" },
  { src: "/icons/quick-actions/billing.png", alt: "Billing" },
  { src: "/icons/quick-actions/reports.png", alt: "Reports" },
  { src: "/icons/quick-actions/sites.png", alt: "Sites" },
  { src: "/icons/quick-actions/workers.png", alt: "Workers" },
] as const;

const loaderVariants = cva("flex flex-col gap-4 items-center justify-center", {
  variants: {
    layout: {
      page: "fixed inset-0 flex-col bg-transparent pointer-events-none",
      inline: "inline-flex gap-2 pt-0",
    },
    size: {
      xxs: "gap-1",
      xs: "gap-1",
      sm: "",
      md: "",
      lg: "",
    },
  },
  defaultVariants: {
    layout: "page",
    size: "md",
  },
});

const loaderContainerVariants = cva("relative overflow-hidden select-none", {
  variants: {
    size: {
      xxs: "h-4 w-4 p-1",
      xs: "h-6 w-6 p-1",
      sm: "h-12 w-12",
      md: "h-16 w-16",
      lg: "h-24 w-24",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const DEFAULT_INTERVAL_MS = 520;

export default function Loader({
  className,
  size,
  layout,
  label = "Loading",
  showLabel,
  intervalMs = DEFAULT_INTERVAL_MS,
}: LoaderProps & VariantProps<typeof loaderVariants>) {
  const effectiveLayout = layout || "page";
  const effectiveShowLabel = showLabel ?? effectiveLayout === "page";
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState<number | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => {
      setCurrentIndex((prev) => {
        setPreviousIndex(prev);
        return (prev + 1) % QUICK_ACTION_ICONS.length;
      });
    }, intervalMs);

    return () => {
      window.clearInterval(id);
    };
  }, [intervalMs]);

  return (
    <div className={cn(loaderVariants({ layout, size }), className)}>
      <div className={cn(loaderContainerVariants({ size }))}>
        {QUICK_ACTION_ICONS.map((icon, index) => {
          const isActive = index === currentIndex;
          const isPrevious = index === previousIndex;

          return (
            <img
              key={icon.src}
              src={icon.src}
              alt={icon.alt}
              className={cn(
                "absolute inset-0 h-full w-full object-contain transition-all duration-200 ease-out",
                isActive && "opacity-100 translate-y-0",
                isPrevious && "opacity-0 -translate-y-4",
                !isActive && !isPrevious && "opacity-0 translate-y-4"
              )}
              draggable={false}
            />
          );
        })}
      </div>
      {effectiveShowLabel ? (
        <span className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
          {label}
        </span>
      ) : null}
    </div>
  );
}
